import React from 'react';
import {
  Box,
  Container,
  Grid,
  Typography,
  Paper,
  Divider,
} from '@mui/material';
import { motion } from 'framer-motion';
import { Phone, Email, LocationOn, AccessTime } from '@mui/icons-material';
import { SectionTitle } from '../../common';
import ContactForm from './ContactForm';
import { fadeInLeft, fadeInRight, viewportConfig } from '../../../utils/animations';
import { CONTACT_INFO } from '../../../utils/constants';

const contactDetails = [
  {
    icon: Phone,
    title: 'Phone',
    details: CONTACT_INFO.phone,
    isArray: true,
  },
  {
    icon: Email,
    title: 'Email',
    details: [CONTACT_INFO.email],
    isArray: true,
  },
  {
    icon: LocationOn,
    title: 'Location',
    details: [CONTACT_INFO.address],
    isArray: true,
  },
  {
    icon: AccessTime,
    title: 'Business Hours',
    details: ['Mon - Fri: 8:00 AM - 6:00 PM', 'Sat: 9:00 AM - 1:00 PM'],
    isArray: true,
  },
];

const Contact = () => {
  return (
    <Box
      id="contact"
      component="section"
      sx={{
        py: { xs: 8, md: 12 },
        backgroundColor: 'background.paper',
      }}
    >
      <Container maxWidth="lg">
        <SectionTitle
          overline="Contact Us"
          title="Get In Touch"
          subtitle="Have questions about our services? We'd love to hear from you. Send us a message and we'll respond as soon as possible."
        />

        <Grid container spacing={4}>
          <Grid item xs={12} md={5}>
            <Box
              component={motion.div}
              initial="hidden"
              whileInView="visible"
              viewport={viewportConfig}
              variants={fadeInLeft}
            >
              <Paper
                elevation={0}
                sx={{
                  p: { xs: 3, md: 4 },
                  height: '100%',
                  backgroundColor: 'primary.main',
                  borderRadius: 3,
                  color: 'white',
                }}
              >
                <Typography
                  variant="h4"
                  sx={{
                    color: 'white',
                    mb: 1,
                    fontWeight: 600,
                  }}
                >
                  Contact Information
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    color: 'rgba(255, 255, 255, 0.8)',
                    mb: 4,
                  }}
                >
                  Reach out to us through any of the following channels
                </Typography>

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                  {contactDetails.map((item, index) => (
                    <Box
                      key={item.title}
                      component={motion.div}
                      initial={{ opacity: 0, x: -20 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: 0.1 + index * 0.1, duration: 0.4 }}
                      sx={{
                        display: 'flex',
                        gap: 2,
                        alignItems: 'flex-start',
                      }}
                    >
                      <Box
                        sx={{
                          width: 44,
                          height: 44,
                          borderRadius: 2,
                          backgroundColor: 'rgba(201, 169, 97, 0.2)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexShrink: 0,
                        }}
                      >
                        <item.icon sx={{ color: 'secondary.main', fontSize: 22 }} />
                      </Box>
                      <Box>
                        <Typography
                          variant="subtitle2"
                          sx={{
                            color: 'secondary.main',
                            fontWeight: 600,
                            mb: 0.5,
                          }}
                        >
                          {item.title}
                        </Typography>
                        {item.details.map((detail, idx) => (
                          <Typography
                            key={idx}
                            variant="body2"
                            sx={{
                              color: 'rgba(255, 255, 255, 0.9)',
                              lineHeight: 1.6,
                            }}
                          >
                            {detail}
                          </Typography>
                        ))}
                      </Box>
                    </Box>
                  ))}
                </Box>

                <Divider
                  sx={{
                    my: 4,
                    borderColor: 'rgba(255, 255, 255, 0.1)',
                  }}
                />

                <Typography
                  variant="body2"
                  sx={{
                    color: 'rgba(255, 255, 255, 0.7)',
                    fontStyle: 'italic',
                  }}
                >
                  We typically respond within 24 hours during business days.
                </Typography>
              </Paper>
            </Box>
          </Grid>

          <Grid item xs={12} md={7}>
            <Box
              component={motion.div}
              initial="hidden"
              whileInView="visible"
              viewport={viewportConfig}
              variants={fadeInRight}
            >
              <Paper
                elevation={0}
                sx={{
                  p: { xs: 3, md: 4 },
                  height: '100%',
                  backgroundColor: 'background.paper',
                  borderRadius: 3,
                  border: '1px solid',
                  borderColor: 'grey.100',
                }}
              >
                <Typography
                  variant="h4"
                  sx={{
                    color: 'text.primary',
                    mb: 1,
                    fontWeight: 600,
                  }}
                >
                  Send Us a Message
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    color: 'text.secondary',
                    mb: 4,
                  }}
                >
                  Fill out the form below and we'll get back to you
                </Typography>

                <ContactForm />
              </Paper>
            </Box>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default Contact;
